import { normalizeProviderSurfaceKey, providerSurfaceLabel } from "./providerSurfaceRegistry";

const normalizeText = (value) => String(value || "").trim();

const compactToken = (value) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const DEFAULT_PLATFORM_LABELS = {
  all: "All Platforms",
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truthsocial: "Truth Social",
  rumble: "Rumble",
  googlesearch: "Google Search",
  chatgptsearch: "ChatGPT Search",
  reddit: "Reddit",
  amazon: "Amazon",
  yahoo_dsp: "Yahoo!",
  stackadapt: "StackAdapt",
  viant: "Viant",
  audio: "Audio",
  ingame: "In-Game",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  email: "Email",
};

const PLATFORM_PRIORITY = [
  "all",
  "facebook",
  "instagram",
  "audience_network",
  "messenger",
  "tiktok",
  "youtube",
  "x",
  "truthsocial",
  "rumble",
  "googlesearch",
  "chatgptsearch",
  "reddit",
  "amazon",
  "yahoo_dsp",
  "stackadapt",
  "viant",
  "audio",
  "ingame",
  "ctv",
  "video",
  "mobile",
  "display",
  "dooh",
  "email",
];

const META_BUNDLE_TOKENS = new Set([
  "meta",
  "metaads",
  "metaadvertising",
  "metapaid",
  "metasocial",
  "metaplacements",
  "metamultiplacement",
  "facebookinstagram",
  "instagramfacebook",
  "fbig",
  "igfb",
]);

const platformLabel = (id, labelForPlatform) => {
  if (typeof labelForPlatform === "function") {
    const label = labelForPlatform(id);
    if (label) return id === "all" && label === "All" ? "All Platforms" : label;
  }
  return DEFAULT_PLATFORM_LABELS[id] || String(id || "").toUpperCase();
};

export const expandOptimizerPlatformIds = (value) => {
  const providerKey = normalizeProviderSurfaceKey(value);
  if (providerKey) return [providerKey];
  const compact = compactToken(value);
  if (!compact) return [];

  const mentionsFacebook = compact.includes("facebook") || compact === "fb";
  const mentionsInstagram = compact.includes("instagram") || compact === "ig";
  if ((mentionsFacebook && mentionsInstagram) || META_BUNDLE_TOKENS.has(compact)) {
    return ["facebook", "instagram"];
  }

  if (compact.includes("audiencenetwork")) return ["audience_network"];
  if (compact.includes("messenger")) return ["messenger"];
  if (mentionsInstagram || compact.includes("metainstagram")) return ["instagram"];
  if (mentionsFacebook || compact.includes("metafacebook")) return ["facebook"];
  if (compact === "tiktok" || compact === "tt") return ["tiktok"];
  if (compact === "youtube" || compact === "yt") return ["youtube"];
  if (compact === "x" || compact === "twitter") return ["x"];
  if (compact === "truthsocial" || compact === "truth") return ["truthsocial"];
  if (compact === "rumble") return ["rumble"];
  if (compact.includes("chatgpt") || compact.includes("openai")) return ["chatgptsearch"];
  if (compact.includes("googlesearch") || compact.includes("paidsearch")) return ["googlesearch"];
  if (compact === "reddit") return ["reddit"];
  if (compact === "amazon" || compact === "amazonads") return ["amazon"];
  if (compact === "yahoo" || compact === "yahoodsp") return ["yahoo_dsp"];
  if (compact === "stackadapt") return ["stackadapt"];
  if (compact === "audio") return ["audio"];
  if (compact === "ingame" || compact === "ingameads") return ["ingame"];
  if (compact === "ctv" || compact === "connectedtv" || compact === "ott") return ["ctv"];
  if (compact === "video" || compact === "olv" || compact === "onlinevideo") return ["video"];
  if (compact === "mobile") return ["mobile"];
  if (compact === "display" || compact === "banner" || compact === "banners") return ["display"];
  if (compact === "dooh") return ["dooh"];
  if (compact === "email" || compact === "mail") return ["email"];
  if (compact === "all") return ["all"];

  return [];
};

export const buildOptimizerPlatformTabs = ({
  tabs = [],
  plannedTabs = [],
  forcedTabs = [],
  extraPlatformKeys = [],
  selectedId = "all",
  labelForPlatform = null,
  isExcludedPlatformId = null,
} = {}) => {
  const byId = new Map();
  const isExcluded =
    typeof isExcludedPlatformId === "function" ? isExcludedPlatformId : () => false;

  const addId = (id, label = null) => {
    if (!id || isExcluded(id)) return;
    if (!byId.has(id)) {
      byId.set(id, { id, label: providerSurfaceLabel(id) || label || platformLabel(id, labelForPlatform) });
    }
  };

  const addRaw = (raw, label = null) => {
    const ids = expandOptimizerPlatformIds(raw);
    ids.forEach((id) => addId(id, ids.length === 1 ? label : null));
  };

  const addTab = (tab) => {
    if (!tab || typeof tab !== "object") return;
    addRaw(tab.id || tab.key || tab.channel || tab.value || tab.label, tab.label);
  };

  [...tabs, ...plannedTabs, ...forcedTabs].forEach(addTab);
  (Array.isArray(extraPlatformKeys) ? extraPlatformKeys : []).forEach((key) => addRaw(key));

  const normalizedSelected = expandOptimizerPlatformIds(selectedId)[0] || compactToken(selectedId);
  if (normalizedSelected && normalizedSelected !== "all") addId(normalizedSelected);
  if (byId.size > 0 && !byId.has("all")) addId("all", "All Platforms");

  return Array.from(byId.values()).sort((a, b) => {
    const ai = PLATFORM_PRIORITY.indexOf(a.id);
    const bi = PLATFORM_PRIORITY.indexOf(b.id);
    if (ai === -1 && bi === -1) return (a.label || "").localeCompare(b.label || "");
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};
